// hooks/useDebounceNavigation.ts
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useRef, useEffect } from "react";

export const useDebounceNavigation = (delay: number = 300) => {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  const pendingPathRef = useRef<string | null>(null);
  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);

  const debouncedNavigate = useCallback((path: string) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Verhindert Spam-Klicks (mehr als 15 Klicks in 3 Sekunden)
    if (timeSinceLastClick < 150) {
      clickCountRef.current++;
      if (clickCountRef.current > 15) {
        console.warn('Navigation rate limit exceeded - cooling down');
        // Längere Cooldown-Zeit bei zu vielen Klicks
        setTimeout(() => {
          clickCountRef.current = 0;
        }, 3000);
        return false;
      }
    } else {
      clickCountRef.current = Math.max(0, clickCountRef.current - 1);
    }
    
    lastClickTimeRef.current = now;

    // Verhindert Navigation zur selben Route
    if (location.pathname === path) {
      return false;
    }

    // Verhindert mehrfache Navigation
    if (isNavigatingRef.current) {
      pendingPathRef.current = path; // Speichert den letzten gewünschten Pfad
      return false;
    }

    // Löscht vorherigen Timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Setzt neuen Timeout mit dynamischem Delay basierend auf Klick-Frequenz
    const dynamicDelay = Math.min(delay + (clickCountRef.current * 50), 1000);
    
    timeoutRef.current = setTimeout(() => {
      // Prüft nochmal ob Navigation noch gewünscht ist
      const finalPath = pendingPathRef.current || path;
      pendingPathRef.current = null;
      
      if (location.pathname === finalPath) {
        return; // Navigation nicht nötig
      }

      isNavigatingRef.current = true;
      
      try {
        navigate(finalPath);
      } catch (error) {
        console.error('Navigation error:', error);
        isNavigatingRef.current = false;
        // Bei Fehler: Längere Pause einlegen
        setTimeout(() => {
          clickCountRef.current = 0;
        }, 2000);
        return;
      }
      
      // Reset nach Navigation mit längerer Wartezeit
      setTimeout(() => {
        isNavigatingRef.current = false;
        // Prüft ob noch eine ausstehende Navigation existiert
        if (pendingPathRef.current && clickCountRef.current < 10) {
          const nextPath = pendingPathRef.current;
          pendingPathRef.current = null;
          // Rekursiver Aufruf mit reduzierter Priorität
          setTimeout(() => {
            debouncedNavigate(nextPath);
          }, 200);
        }
      }, 200);
    }, dynamicDelay);

    return true;
  }, [navigate, location.pathname, delay]);

  const isNavigating = () => isNavigatingRef.current;
  const hasPendingNavigation = () => pendingPathRef.current !== null;

  // Automatisches Cleanup und Reset
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (now - lastClickTimeRef.current > 5000) {
        clickCountRef.current = 0; // Reset nach 5 Sekunden Inaktivität
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    isNavigatingRef.current = false;
    pendingPathRef.current = null;
    clickCountRef.current = 0;
  }, []);

  // Reset Click Counter periodisch
  const resetClickCounter = useCallback(() => {
    clickCountRef.current = 0;
    console.log('Navigation counter reset');
  }, []);

  return { 
    debouncedNavigate, 
    isNavigating, 
    hasPendingNavigation, 
    cleanup,
    resetClickCounter 
  };
};

// Zusätzlicher Hook für globale Navigation mit Request Cancellation
export const useGlobalNavigationProtection = () => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, []);

  const getAbortSignal = useCallback(() => {
    if (!abortControllerRef.current) {
      abortControllerRef.current = new AbortController();
    }
    return abortControllerRef.current.signal;
  }, []);

  return { cancelPendingRequests, getAbortSignal };
};